import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, MapPin, Ticket, type LucideIcon } from 'lucide-react';
import type { GetPlacesToExploreResponse, GuestPlace, GuestTourPackage } from '@hotel-voice/shared';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState, StateView, UnavailableState } from '@/components/common/StateView';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { getPlacesToExplore } from '@/services/firebase/callables';
import { toUserMessage } from '@/utils/errors';
import { PlaceCard, PlaceDetail, TourCard, TourDetail } from './ExploreCards';

type Loaded = { key: string; data: GetPlacesToExploreResponse | null; error: string | null };

/**
 * Fetches once per room and per retry. The result is tagged with the key it
 * was fetched for, the way `GuestSessionContext` tags the catalogue, so a retry
 * shows the skeleton rather than the previous error.
 */
function usePlacesToExplore() {
  const { location } = useReadyGuest();
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const key = `${location.hotelId}/${location.roomId}#${attempt}`;

  useEffect(() => {
    let cancelled = false;
    getPlacesToExplore({ hotelId: location.hotelId, roomId: location.roomId })
      .then((data) => {
        if (!cancelled) setLoaded({ key, data, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoaded({ key, data: null, error: toUserMessage(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [key, location.hotelId, location.roomId]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const settled = loaded !== null && loaded.key === key;
  return {
    phase: !settled ? 'loading' : loaded.error ? 'error' : 'ready',
    data: settled ? loaded.data : null,
    error: settled ? loaded.error : null,
    retry,
  } as const;
}

/**
 * Places to Explore: the hotel's concierge desk on the guest's phone. Two
 * tabs — nearby places, nearest first, and the tour packages the hotel
 * offers — as large photo cards; tapping one opens a sheet with every photo
 * and the full text.
 *
 * Nothing is ordered from here. A place links out to Maps; a package's call
 * to action is reception, which books it. The server has already dropped
 * disabled items and answers `enabled: false` when the service is off.
 */
export function PlacesToExplorePage() {
  const { phase, data, error, retry } = usePlacesToExplore();
  const [tab, setTab] = useState<string | null>(null);
  const [openPlace, setOpenPlace] = useState<GuestPlace | null>(null);
  const [openTour, setOpenTour] = useState<GuestTourPackage | null>(null);

  const body = () => {
    if (phase === 'loading') return <CardsSkeleton />;
    if (phase === 'error' || !data) {
      return <ErrorState message={error ?? 'Could not load places to explore.'} onRetry={retry} />;
    }
    if (!data.enabled) return <UnavailableState service="Places to Explore" />;

    const { places, tourPackages } = data;
    if (places.length === 0 && tourPackages.length === 0) {
      return (
        <FriendlyEmpty
          icon={Compass}
          title="Nothing listed just yet"
          message="Our team knows the area well — ask reception for their favourite spots and tours."
        />
      );
    }

    // Open on whichever tab has something, places first.
    const current = tab ?? (places.length > 0 ? 'places' : 'tours');

    return (
      <>
        <Hero />
        <Tabs value={current} onValueChange={setTab} className="px-4 pt-4 pb-6">
          <TabsList className="w-full">
            <TabsTrigger value="places" className="flex-1 [&>svg]:size-4">
              <MapPin aria-hidden="true" />
              Places
              <Count n={places.length} />
            </TabsTrigger>
            <TabsTrigger value="tours" className="flex-1 [&>svg]:size-4">
              <Ticket aria-hidden="true" />
              Tour Packages
              <Count n={tourPackages.length} />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="places" className="flex flex-col gap-4">
            {places.length === 0 ? (
              <FriendlyEmpty
                icon={MapPin}
                title="No places listed yet"
                message="Reception can point you to the best sights nearby."
              />
            ) : (
              // Two tiles a row; the shell is phone-width (max-w-md) at every size.
              <div className="grid grid-cols-2 gap-3">
                {places.map((place) => (
                  <PlaceCard key={place.id} place={place} onOpen={() => setOpenPlace(place)} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="tours" className="flex flex-col gap-4">
            {tourPackages.length === 0 ? (
              <FriendlyEmpty
                icon={Ticket}
                title="No tour packages right now"
                message="Ask reception — they can still help you plan a day out."
              />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {tourPackages.map((pkg) => (
                    <TourCard key={pkg.id} pkg={pkg} onOpen={() => setOpenTour(pkg)} />
                  ))}
                </div>
                <p className="text-muted-foreground px-1 text-center text-xs">
                  To book a tour, call reception — they will confirm timings and pickup.
                </p>
              </>
            )}
          </TabsContent>
        </Tabs>

        <PlaceDetail place={openPlace} onOpenChange={(open) => !open && setOpenPlace(null)} />
        <TourDetail pkg={openTour} onOpenChange={(open) => !open && setOpenTour(null)} />
      </>
    );
  };

  return (
    <AppShell>
      <PageHeader title="Places to Explore" subtitle="Your local guide" />
      {body()}
    </AppShell>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[radial-gradient(120%_120%_at_100%_0%,oklch(0.6_0.12_200)_0%,oklch(0.38_0.12_240)_65%,oklch(0.28_0.08_255)_100%)] px-5 py-6 text-white">
      <Compass className="absolute -top-6 -right-6 size-36 text-white/10" aria-hidden="true" strokeWidth={1.25} />
      <p className="text-[11px] font-semibold tracking-widest text-white/75 uppercase">Concierge picks</p>
      <h2 className="mt-1 text-xl leading-tight font-bold">Discover what’s around you</h2>
      <p className="mt-1 max-w-[18rem] text-sm text-white/85">
        Hand-picked sights near the hotel and tours we can arrange for you.
      </p>
    </section>
  );
}

function Count({ n }: { n: number }) {
  return (
    <span className="bg-muted-foreground/15 rounded-full px-1.5 text-[11px] leading-4 font-semibold tabular-nums">
      {n}
    </span>
  );
}

function FriendlyEmpty({ icon, title, message }: { icon: LucideIcon; title: string; message: string }) {
  return (
    <StateView icon={icon} title={title} message={message} className="py-10">
      <Button asChild>
        <Link to="/call">Call Reception</Link>
      </Button>
      <Button asChild variant="ghost">
        <Link to="/">Back to home</Link>
      </Button>
    </StateView>
  );
}

function CardsSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4" aria-busy="true" aria-label="Loading">
      <Skeleton className="h-28 w-full rounded-2xl" />
      <Skeleton className="h-10 w-full rounded-full" />
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="overflow-hidden rounded-2xl border">
            <Skeleton className="aspect-[4/3] w-full rounded-none" />
            <div className="flex flex-col gap-2 p-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
